import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from '../../contexts/FormContext'
import { Button, SaveButton } from '../../components/common'
import { Checkbox } from '../../components/forms'
import { getTemplateById } from '../../data/templates'
import { submitRequest, updateRequest } from '../../services/api'
import './Step7Review.css'

function Step7Review() {
  const navigate = useNavigate()
  const { formData, updateNestedFormData, goToPreviousStep, clearFormData, editMode, editingRequestId } = useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      if (editMode && editingRequestId) {
        // Update existing request
        console.log('Updating request:', editingRequestId)
        const result = await updateRequest(editingRequestId, formData)
        console.log('Update successful:', result)

        // Clear localStorage BEFORE navigating (skip navigation)
        clearFormData(true)

        // Get the access token from formData (should be there from when we loaded for edit)
        const token = formData.accessToken || result.data.accessToken

        // Navigate to thank you page with token
        navigate(`/thank-you/${token}`)
      } else {
        // Submit new request
        console.log('Submitting new request:', formData)
        const result = await submitRequest(formData)
        console.log('Submission successful:', result)

        // Clear localStorage BEFORE navigating (skip navigation)
        clearFormData(true)

        // Navigate to thank you page with secure token
        navigate(`/thank-you/${result.data.accessToken}`)
      }
    } catch (error) {
      console.error('Submission failed:', error)
      setSubmitError(error.message || 'Failed to submit request. Please try again.')
      setIsSubmitting(false)
    }
  }

  const canSubmit = formData.agreements.agreesToTerms

  const formatCurrency = (amount) => {
    return `¥${amount.toLocaleString('ja-JP')}`
  }

  const getPackageName = () => {
    return formData.siteBase === 'basic' ? 'Basic Corporate Website' : 'Advanced Ecommerce Site'
  }

  const getDesignApproach = () => {
    if (formData.designApproach === 'template') {
      const template = formData.selectedTemplate ? getTemplateById(formData.selectedTemplate) : null
      return template ? `Template: ${template.name}` : 'Template-Based Design'
    }
    return 'Custom Design'
  }

  const getLogoOption = () => {
    if (formData.logoOption === 'have_logo') return 'Using existing logo'
    if (formData.logoOption === 'need_development') return 'Logo development (+¥50,000, +2 weeks)'
    if (formData.logoOption === 'provide_later') return 'Will provide logo later'
    return 'Not specified'
  }

  const getTotalPages = () => {
    return formData.pages.default.length + formData.pages.additional.length
  }

  const getSelectedAddons = () => {
    const addons = []

    if (formData.addons.domain.option === 'register_new') {
      addons.push(`Domain: ${formData.addons.domain.newDomainName}${formData.addons.domain.newDomainExtension}`)
    } else if (formData.addons.domain.option === 'existing') {
      addons.push(`Domain: ${formData.addons.domain.existingDomain}`)
    } else {
      addons.push('Domain: To be decided')
    }

    if (formData.addons.managedSupport.selected) addons.push('Managed Support (¥25k/month)')
    if (formData.addons.corporateEmail.selected) addons.push(`Corporate Email (${formData.addons.corporateEmail.users} users)`)
    if (formData.addons.websiteChat.selected) addons.push(`Website Chat (${formData.addons.websiteChat.users} users)`)
    if (formData.addons.appointmentScheduler.selected) addons.push(`Scheduler (${formData.addons.appointmentScheduler.users} users)`)
    if (formData.addons.businessCards.selected) addons.push('Business Cards (500 cards)')

    return addons
  }

  return (
    <div className="step-section">
      {/* Section Header */}
      <div className="section-header">
        <h2>Step 7: Review Your Request</h2>
        <p className="section-subtitle">Please review all details before submitting</p>
      </div>

      {/* Section Body */}
      <div className="section-body">
        {/* Review Cards */}
        <div className="review-cards">
          {/* Website Package */}
          <div className="review-card-minimal">
            <div className="review-card-header-minimal">
              <div className="review-icon">🌐</div>
              <h3 className="review-card-title-minimal">Website Package</h3>
            </div>
            <div className="review-items">
              <div className="review-item-minimal">
                <span className="review-label-minimal">Package:</span>
                <span className="review-value-minimal">{getPackageName()}</span>
              </div>
              <div className="review-item-minimal">
                <span className="review-label-minimal">Design:</span>
                <span className="review-value-minimal">{getDesignApproach()}</span>
              </div>
              <div className="review-item-minimal">
                <span className="review-label-minimal">Logo:</span>
                <span className="review-value-minimal">{getLogoOption()}</span>
              </div>
            </div>
          </div>

          {/* Pages */}
          <div className="review-card-minimal">
            <div className="review-card-header-minimal">
              <div className="review-icon">📄</div>
              <h3 className="review-card-title-minimal">Website Pages</h3>
            </div>
            <div className="review-items">
              <div className="review-item-minimal">
                <span className="review-label-minimal">Total Pages:</span>
                <span className="review-value-minimal">{getTotalPages()} pages</span>
              </div>
              {formData.pages.additional.length > 0 && (
                <div className="review-item-minimal">
                  <span className="review-label-minimal">Additional:</span>
                  <span className="review-value-minimal">
                    {formData.pages.additional.map(p => p.name).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Add-ons */}
          <div className="review-card-minimal">
            <div className="review-card-header-minimal">
              <div className="review-icon">⚡</div>
              <h3 className="review-card-title-minimal">Add-ons & Services</h3>
            </div>
            <div className="review-items">
              <ul className="review-list-minimal">
                {getSelectedAddons().map((addon, idx) => (
                  <li key={idx}>{addon}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div className="review-card-minimal">
            <div className="review-card-header-minimal">
              <div className="review-icon">👤</div>
              <h3 className="review-card-title-minimal">Contact Information</h3>
            </div>
            <div className="review-items">
              <div className="review-item-minimal">
                <span className="review-label-minimal">Company:</span>
                <span className="review-value-minimal">{formData.contact.companyName}</span>
              </div>
              <div className="review-item-minimal">
                <span className="review-label-minimal">Contact:</span>
                <span className="review-value-minimal">{formData.contact.contactName}</span>
              </div>
              <div className="review-item-minimal">
                <span className="review-label-minimal">Email:</span>
                <span className="review-value-minimal">{formData.contact.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Summary */}
        <div className="cost-summary-box">
          <h3 className="cost-summary-title">💰 Cost Estimate Summary</h3>
          <div className="cost-total-display">
            <div className="cost-total-row">
              <span>Total Setup Cost:</span>
              <span className="cost-amount">{formatCurrency(formData.costs.setupTotal)}</span>
            </div>
            {formData.costs.monthlyRecurring > 0 && (
              <div className="cost-monthly-row">
                <span>Monthly Recurring:</span>
                <span className="cost-amount-small">{formatCurrency(formData.costs.monthlyRecurring)}</span>
              </div>
            )}
          </div>
          <p className="estimate-note">* Final costs confirmed in detailed proposal</p>
        </div>

        {/* Next Steps */}
        <div className="next-steps-box">
          <div className="next-steps-header">
            <div className="next-steps-icon">💳</div>
            <h3>What Happens Next?</h3>
          </div>
          <p className="next-steps-deposit">
            <strong>Deposit Required:</strong> ¥10,000 to begin the project
          </p>
          <p className="deposit-refund-note">
            💡 Deposit is fully refundable if cancelled within 7 days of request submission
          </p>
          <ul className="next-steps-list">
            <li>We'll review your request within 1 business day</li>
            <li>You'll receive a detailed proposal with project timeline</li>
            <li>Contract and deposit invoice sent to your email</li>
            <li>Project begins once deposit is received</li>
          </ul>
        </div>

        {/* Simple Agreement */}
        <div className="agreement-section">
          <h3 className="agreement-title">✓ Final Agreement</h3>
          <div className="agreement-box">
            <Checkbox
              id="agree-terms"
              label="I understand the pricing and timeline estimates. I agree to StartupHive's Terms of Service and acknowledge that a ¥10,000 deposit is required to begin the project."
              checked={formData.agreements.agreesToTerms}
              onChange={(e) => updateNestedFormData('agreements.agreesToTerms', e.target.checked)}
            />
          </div>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="submit-error">
            ⚠️ {submitError}
          </div>
        )}

        {/* Submit Button */}
        <div className="submit-button-section">
          <Button
            variant="primary"
            size="lg"
            disabled={!canSubmit || isSubmitting}
            onClick={handleSubmit}
            className="submit-button-large"
          >
            {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Request'}
          </Button>
          <p className="submit-helper">You'll receive a proposal and invoice within 1 business day</p>
        </div>
      </div>

      {/* Section Actions */}
      <div className="section-actions">
        <Button variant="secondary" size="md" onClick={goToPreviousStep}>
          ← Back
        </Button>
        <div></div>
      </div>
    </div>
  )
}

export default Step7Review
